/**
 * Parse URL parameters from the search params object
 * Handles arrays (comma-separated values) and nested parameters (using bracket notation)
 */
export function parseUrlParams(searchParams: URLSearchParams): Record<string, any> {
  const parsedParams: Record<string, any> = {};
  const extraParams: Record<string, any> = {};

  // Process each parameter
  for (const [key, value] of searchParams.entries()) {
    // Handle extra[] parameters - they're stored in a nested object
    if (key.startsWith('extra[') && key.endsWith(']')) {
      const extraKey = key.slice(6, -1); // Extract the key inside the brackets

      // Convert numeric extra parameters to numbers
      if (['project_id', 'tmp_project_id', 'view_id', 'project_view_id'].includes(extraKey)) {
        extraParams[extraKey] = parseInt(value, 10) || 0;
      } else {
        extraParams[extraKey] = value;
      }

      continue;
    }

    // Handle special parameters that should be parsed as arrays
    if (key === 'style_codes' || key === 'style_ids') {
      parsedParams[key] = value ? value.split(',') : [];
    }
    // Handle boolean parameters
    else if (key === 'default_renovation' || key === 'auto_declutter') {
      parsedParams[key] = value === 'true';
    }
    // Handle numeric parameters
    else if (key === 'upscale_resolution') {
      parsedParams[key] = parseInt(value, 10) || 1; // Default to 1x if invalid
    }
    // Everything else is a string
    else {
      parsedParams[key] = value;
    }
  }

  // If we have extra parameters, add them to the parsed parameters
  if (Object.keys(extraParams).length > 0) {
    parsedParams.extra = extraParams;
  }

  return parsedParams;
}

/**
 * Build a URL with the given parameters
 */
export function buildUrl(baseUrl: string, params: Record<string, any>): string {
  const searchParams = new URLSearchParams();
  
  for (const [key, value] of Object.entries(params)) {
    // Skip undefined values
    if (value === undefined) continue;
    
    // Handle extra parameters (nested object)
    if (key === 'extra' && typeof value === 'object') {
      for (const [extraKey, extraValue] of Object.entries(value)) {
        if (extraValue !== undefined) {
          searchParams.append(`extra[${extraKey}]`, String(extraValue));
        }
      }
      continue;
    }
    
    // Handle arrays (convert to comma-separated string)
    if (Array.isArray(value)) {
      if (value.length > 0) {
        searchParams.append(key, value.join(','));
      }
    }
    // Handle booleans
    else if (typeof value === 'boolean') {
      searchParams.append(key, value.toString());
    }
    // Everything else is converted to string
    else if (value !== null && value !== undefined) {
      searchParams.append(key, String(value));
    }
  }
  
  const queryString = searchParams.toString();
  if (queryString) {
    return `${baseUrl}?${queryString}`;
  }
  return baseUrl;
}